import { useEffect, useState } from "react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGES = `${BACKEND_URL}/api/pages`;

function prettifyJob(base) {
  const parts = base.split("_");
  const code = parts[0] || base;
  const meta = parts
    .slice(1)
    .map((tok) => {
      const m = tok.match(/^([a-zA-Z]+)(\d+)$/);
      if (m) {
        const labels = { custom: "Beef Carton" };
        const word = labels[m[1].toLowerCase()] || m[1][0].toUpperCase() + m[1].slice(1);
        return word + " " + m[2] + " mm";
      }
      return tok;
    })
    .join(" · ");
  return { code, meta };
}

const Arrow = () => (
  <svg className="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" /><path d="m13 6 6 6-6 6" />
  </svg>
);

export default function App() {
  const [pages, setPages] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Jackstone | Freeze-time Reports";
    fetch(`${API}/pages-list`, { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => setPages((d && d.pages) || []))
      .catch(() => setError(true));
  }, []);

  const tools = (pages || []).filter((f) => f === "freeze_dashboard.html");
  const reportFiles = (pages || []).filter((f) => f !== "freeze_dashboard.html");

  const groups = {};
  reportFiles.forEach((f) => {
    const key = f.replace(/\.html$/i, "").replace(/_(energyproof|hold40)$/i, "");
    (groups[key] = groups[key] || []).push(f);
  });
  const jobKeys = Object.keys(groups).sort();

  return (
    <div className="js-app" data-testid="landing-root">
      <header className="js-header" data-testid="site-header">
        <div className="js-header-inner">
          <div className="js-brand">
            <img
              className="js-logo"
              src="https://brand.jackstone.com/logo-white.png"
              alt="Jackstone"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <span className="js-tagline">Frozen Today, Fresh Tomorrow</span>
          </div>
          <span className="js-pill" data-testid="header-pill">Report Library</span>
        </div>
      </header>

      <main className="js-main">
        <div className="js-hero">
          <h1 className="js-h1">Freeze-time Reports</h1>
          <p className="js-sub">
            Interactive plate-freezer calculator and per-project freeze-time &amp; energy
            documents. Select a report below.
          </p>
        </div>

        {error && (
          <div className="js-note js-note-err" data-testid="load-error">
            Couldn&apos;t load the report list. Please refresh the page.
          </div>
        )}

        {!error && pages === null && (
          <div className="js-note" data-testid="loading">Loading reports…</div>
        )}

        {!error && pages !== null && (
          <>
            {tools.length > 0 && (
              <>
                <div className="js-eyebrow">Calculator</div>
                <div className="js-grid">
                  {tools.map((f) => (
                    <article className="js-card js-card-tool" key={f} data-testid="card-calculator">
                      <div className="js-card-strip">
                        <span>Interactive Tool</span>
                      </div>
                      <div className="js-card-body">
                        <h3 className="js-card-title">Freeze-time Calculator</h3>
                        <p className="js-card-meta">
                          Live plate-freezer model — composition, geometry, cooling load &amp;
                          method comparison, with PDF / print export.
                        </p>
                        <div className="js-links">
                          <a className="js-btn js-btn-primary" data-testid="open-calculator"
                            href={`${PAGES}/${f}`} target="_blank" rel="noopener noreferrer">
                            <span>Open calculator</span><Arrow />
                          </a>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            )}

            {jobKeys.length > 0 && (
              <>
                <div className="js-eyebrow">Project Reports</div>
                <div className="js-grid">
                  {jobKeys.map((key) => {
                    const files = groups[key];
                    const main = files.find((f) => !/_(energyproof|hold40)\.html$/i.test(f));
                    const proof = files.find((f) => /_energyproof\.html$/i.test(f));
                    const hold = files.find((f) => /_hold40\.html$/i.test(f));
                    const { code, meta } = prettifyJob(key);
                    return (
                      <article className="js-card" key={key} data-testid={`card-${code}`}>
                        <div className="js-card-strip">
                          <span>{code}</span>
                        </div>
                        <div className="js-card-body">
                          <h3 className="js-card-title">{meta || key}</h3>
                          <p className="js-card-meta">
                            Project freeze-time analysis{proof ? " & energy conservation proof" : ""}.
                          </p>
                          <div className="js-links">
                            {main && (
                              <a className="js-btn js-btn-primary" data-testid={`open-report-${code}`}
                                href={`${PAGES}/${main}`} target="_blank" rel="noopener noreferrer">
                                <span>Freeze-time report</span><Arrow />
                              </a>
                            )}
                            {hold && (
                              <a className="js-btn js-btn-primary" data-testid={`open-hold-${code}`}
                                href={`${PAGES}/${hold}`} target="_blank" rel="noopener noreferrer">
                                <span>COP Limit Lower TE Mode</span><Arrow />
                              </a>
                            )}
                            {proof && (
                              <a className="js-btn js-btn-ghost" data-testid={`open-proof-${code}`}
                                href={`${PAGES}/${proof}`} target="_blank" rel="noopener noreferrer">
                                <span>Energy conservation proof</span><Arrow />
                              </a>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}

            {tools.length === 0 && jobKeys.length === 0 && (
              <div className="js-note" data-testid="empty">No reports available yet.</div>
            )}
          </>
        )}
      </main>

      <footer className="js-footer">
        JACKSTONE — BETA SOFTWARE FOR TRIAL USE ONLY — TECHNICAL CONTACT:
        KYLE@JACKSTONE.CO.UK (PREFERABLY PRAISE OVER PROBLEMS)
      </footer>
    </div>
  );
}
